import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AssessmentMockService } from '../services/assessment-mock.service';
import { DEMOGRAPHIC_OPTIONS, LANGUAGES, PRACTICE_ITEM } from '../data/assessment.seed';
import { ScaleValue } from '../models/assessment.model';

@Component({
  selector: 'app-assessment-landing',
  templateUrl: './assessment-landing.component.html',
  styleUrls: ['./assessment-landing.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AssessmentLandingComponent implements OnInit, OnDestroy {
  token!: string;
  state: 'ok' | 'invalid' | 'closed' = 'ok';
  welcomeName = '';
  form!: FormGroup;
  demo = DEMOGRAPHIC_OPTIONS;
  languages = LANGUAGES;
  practice = PRACTICE_ITEM;
  practiceValue: ScaleValue | null = null;

  private destroy$ = new Subject<void>();

  constructor(private route: ActivatedRoute, private router: Router,
              private fb: FormBuilder, private svc: AssessmentMockService) {}

  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get('token')!;
    const stage = this.svc.stageFor(this.token);
    if (stage === 'invalid' || stage === 'closed') { this.state = stage; return; }
    this.welcomeName = this.svc.invitationFor(this.token)?.name ?? '';
    this.form = this.fb.group({
      language: ['English', Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      emailConfirmation: ['', Validators.required],
      termsAccepted: [false, Validators.requiredTrue],
      educationLevel: [''], industry: [''], fieldOfStudy: [''],
      orgLevel: [''], jobFunction: [''], birthYear: [''], gender: [''],
    });

    // Work branches the survey: "Student" answers field-of-study instead of industry and role.
    // Clear the hidden branch so an answer switched away from never submits.
    this.form.get('jobFunction')!.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((work: string) => {
      const cleared = work === 'Student' ? { industry: '', orgLevel: '' } : { fieldOfStudy: '' };
      this.form.patchValue(cleared, { emitEvent: false });
    });
  }

  /** Selecting "Student" under Work replaces industry + role with field-of-study. */
  get isStudent(): boolean { return this.form?.value.jobFunction === 'Student'; }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showError(name: string): boolean {
    const c = this.form.get(name);
    return !!c && c.invalid && c.touched;
  }
  emailMismatch(): boolean {
    const { email, emailConfirmation } = this.form.value;
    return !!emailConfirmation && this.form.get('emailConfirmation')!.touched && email !== emailConfirmation;
  }

  start(): void {
    if (this.form.invalid || this.emailMismatch()) {
      this.form.markAllAsTouched();
      const first = document.querySelector('.asmt-card .ng-invalid') as HTMLElement | null;
      first?.focus();
      return;
    }
    const v = this.form.value;
    this.svc.register(this.token, {
      language: v.language, firstName: v.firstName, lastName: v.lastName,
      email: v.email, termsAccepted: v.termsAccepted,
      demographics: {
        educationLevel: v.educationLevel || undefined,
        industry: v.industry || undefined,
        fieldOfStudy: v.fieldOfStudy || undefined,
        orgLevel: v.orgLevel || undefined,
        jobFunction: v.jobFunction || undefined,
        birthYear: v.birthYear || undefined,
        gender: v.gender || undefined,
      },
    });
    const next = this.svc.stageFor(this.token); // 'pay' or 'questions'
    this.router.navigate(['/assessments', this.token, next === 'pay' ? 'pay' : 'q']);
  }
}
